// Client-side half of the `_users` doc version-affinity rule (#4283).
//
// The officer-only methods in `services/auth.service.ts` open a remote database
// wrapper on `_users` and read and write it straight from the browser, so the
// server's own refusal never sees them. A stale cached PWA could otherwise
// read, change and write back a shape it was never taught. Angular-free so it
// stays node-testable, like `services/couch-login-error-classify.ts`.
//
// See `design-docs/users-doc-versioning.md` for the whole scheme.

import {
  CouchUserInfoBase,
  getUsersDocFormatVersion,
  isUsersDocTooNewToRead,
  MAX_SUPPORTED_USERS_DOC_FORMAT_VERSION,
} from '@cf-shared/data-model/couch-user';

/**
 * Thrown when this app build meets a `_users` document written in a format
 * version it does not understand. Refusing loudly is the whole point: the
 * alternative is reading fields whose meaning may have changed, and then
 * writing the misread result back over the real data.
 *
 * The message is deliberately plain English rather than a translated string:
 * every path that can raise it is an officer/admin-only screen, and those
 * screens are untranslated by design.
 */
export class UsersDocTooNewForClientError extends Error {
  /** Format version recorded on the document we refused to touch. */
  readonly docVersion: number;
  /** Highest format version this app build knows how to read. */
  readonly maxSupportedVersion: number;

  constructor(docDescription: string, docVersion: number) {
    super(
      `This app version cannot safely read the account record for ${docDescription}. `
      + `The record is stored in format version ${docVersion}, and this app understands `
      + `only up to version ${MAX_SUPPORTED_USERS_DOC_FORMAT_VERSION}. `
      + `Reload the page (or install the latest app build) and try again.`,
    );
    this.name = 'UsersDocTooNewForClientError';
    this.docVersion = docVersion;
    this.maxSupportedVersion = MAX_SUPPORTED_USERS_DOC_FORMAT_VERSION;
  }
}

/**
 * Refuse to work with a `_users` document that is newer than this app build
 * understands. Call this on every `_users` document the client reads directly,
 * and on every one it is about to write directly.
 *
 * `docDescription` names the account in the thrown message (an email address,
 * or the CouchDB user name when that is all we have), so the officer reading
 * the error knows which record was refused.
 *
 * An absent document is fine and is not an error: there is no shape to
 * misread, and the callers already treat "not found" as a normal answer.
 */
export function assertUsersDocVersionSupportedOnClient(
  doc: Pick<CouchUserInfoBase, 'usersDocFormatVersion'> | null | undefined,
  docDescription: string,
): void {
  // if there is no document, there is nothing that could be misread
  if (!doc) {
    return;
  }
  // if the document claims a newer format than this build knows, refuse it
  if (isUsersDocTooNewToRead(doc)) {
    throw new UsersDocTooNewForClientError(docDescription, getUsersDocFormatVersion(doc));
  }
}
